const BASE_URL = '/api/admin';

async function get(url) {
    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
    });
    return response;
}

async function putData(url, data = {}) {
    const response = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify(data),
    });
    return response;
}

async function post(url) {
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        redirect: 'error',
    });
    return response;
}

async function postData(url, data = {}) {
    const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
        redirect: 'error',
        body: JSON.stringify(data),
    });
    return response;
}

async function del(url) {
    const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include',
    });
    return response;
}

class AdminApiClient {
    static async login(email, password) {
        const response = await postData(`${BASE_URL}/passwordLogin`, {
            email,
            password,
        });
        return AdminApiClient.handleResponse(response);
    }

    static async logout() {
        const response = await post(`${BASE_URL}/logout`);
        return AdminApiClient.handleResponse(response);
    }

    static async getStats(setStatsResult) {
        const response = await get(`${BASE_URL}/stats`);
        const result = await AdminApiClient.handleResponse(response);
        if (setStatsResult && result.status === 200 && result.data) {
            setStatsResult(result.data);
        }
        return result;
    }

    static async search(searchTerm, setSearchResults, searchCustomers = true) {
        searchTerm = encodeURIComponent(searchTerm.trim());

        const response = await get(
            `${BASE_URL}/search?q=${searchTerm}&customers=${searchCustomers}`,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setSearchResults && result.data) {
            setSearchResults(result.data);
        }
        return result;
    }

    static async getUser(userId, setUserResult) {
        const response = await get(`${BASE_URL}/user/${userId}`);
        const result = await AdminApiClient.handleResponse(response);
        if (setUserResult && result.data) {
            setUserResult(result.data);
        }
        return result;
    }

    static async createUser(user, setUserResult) {
        const response = await postData(`${BASE_URL}/user`, user);
        const result = await AdminApiClient.handleResponse(response);
        if (setUserResult && result.data) {
            setUserResult(result.data);
        }
        return result;
    }

    static async updateUser(user) {
        const response = await putData(`${BASE_URL}/user/${user.id}`, user);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async deleteUser(userId) {
        const response = await del(`${BASE_URL}/user/${userId}`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async deleteUserTotp(userId) {
        const response = await del(`${BASE_URL}/user/${userId}/totp`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async clearUserSessions(userId) {
        const response = await del(`${BASE_URL}/user/${userId}/sessions`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async deleteUserCredential(userId, credentialId) {
        const response = await del(
            `${BASE_URL}/user/${userId}/credential/${credentialId}`,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async addUserPassword(userId) {
        const response = await post(`${BASE_URL}/user/${userId}/password`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async getCustomer(customerId, setCustomerResult) {
        const response = await get(`${BASE_URL}/customer/${customerId}`);
        const result = await AdminApiClient.handleResponse(response);
        if (setCustomerResult && result.data) {
            setCustomerResult(result.data);
        }
        return result;
    }

    static async getCustomerWithoutChangingState(customerId) {
        const response = await get(`${BASE_URL}/customer/${customerId}`);
        const result = await AdminApiClient.handleResponse(response);
        return result.data;
    }

    static async createCustomer(customer, setCustomerResult) {
        const response = await postData(`${BASE_URL}/customer`, customer);
        const result = await AdminApiClient.handleResponse(response);
        if (setCustomerResult && result.data) {
            setCustomerResult(result.data);
        }
        return result;
    }

    static async updateCustomer(customer, setCustomerResult) {
        const response = await putData(
            `${BASE_URL}/customer/${customer.id}`,
            customer,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setCustomerResult && result.data) {
            setCustomerResult(result.data);
        }
        return result;
    }

    static async mergeCustomers(targetCustomerId, sourceCustomerId) {
        const response = await postData(
            `${BASE_URL}/customer/${targetCustomerId}/merge`,
            { sourceCustomerId },
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async deleteCustomer(customerId) {
        const response = await del(`${BASE_URL}/customer/${customerId}`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async getSsoConfig(customerId, setSsoConfigResult) {
        const response = await get(
            `${BASE_URL}/customer/${customerId}/ssoConfig`,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setSsoConfigResult && result.data) {
            setSsoConfigResult(result.data.error ? undefined : result.data);
        }
        return result;
    }

    static async createSsoConfig(customerId, ssoConfig, setSsoConfigResult) {
        const response = await postData(
            `${BASE_URL}/customer/${customerId}/ssoConfig`,
            ssoConfig,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setSsoConfigResult && result.data) {
            setSsoConfigResult(result.data);
        }
        return result;
    }

    static async updateSsoConfig(customerId, ssoConfig, setSsoConfigResult) {
        const response = await putData(
            `${BASE_URL}/customer/${customerId}/ssoConfig`,
            ssoConfig,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setSsoConfigResult && result.data) {
            setSsoConfigResult(result.data);
        }
        return result;
    }

    static async deleteSsoConfig(customerId) {
        const response = await del(
            `${BASE_URL}/customer/${customerId}/ssoConfig`,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async createTeam(team, setTeamResult) {
        const response = await postData(`${BASE_URL}/team`, team);
        const result = await AdminApiClient.handleResponse(response);
        if (setTeamResult && result.data) {
            setTeamResult(result.data);
        }
        return result;
    }

    static async updateTeam(team, setTeamResult) {
        const response = await putData(`${BASE_URL}/team/${team.id}`, team);
        const result = await AdminApiClient.handleResponse(response);
        if (setTeamResult && result.data) {
            setTeamResult(result.data);
        }
        return result;
    }

    static async deleteTeam(teamId) {
        const response = await del(`${BASE_URL}/team/${teamId}`);
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async createTeamMember(teamId, member) {
        const response = await postData(
            `${BASE_URL}/team/${teamId}/member`,
            member,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async inviteTeamMember(teamId, member) {
        const response = await postData(
            `${BASE_URL}/team/${teamId}/invite`,
            member,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async updateTeamMember(teamId, member) {
        const response = await putData(
            `${BASE_URL}/team/${teamId}/member/${member.id}`,
            member,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async removeTeamMember(teamId, memberId) {
        const response = await del(
            `${BASE_URL}/team/${teamId}/member/${memberId}`,
        );
        const result = await AdminApiClient.handleResponse(response);
        return result;
    }

    static async getReport(reportName, type, payments, setReportResults) {
        const response = await get(
            `${BASE_URL}/report/${reportName}?type=${type}&payments=${payments}`,
        );
        const result = await AdminApiClient.handleResponse(response);
        if (setReportResults && result.data) {
            setReportResults(result.data);
        }
        return result;
    }

    static async handleResponse(response) {
        if (response.ok) {
            if (response.status !== 204) {
                response.data = await response.json();
            }

            return response;
        }

        response.data = await response.json();
        console.error(response);

        if (response.status === 401) {
            console.log('Unauthorized');
            window.location.replace('/#/login');
        }

        return response;
    }
}

export default AdminApiClient;
